import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useGeneralFormStore } from "@/stores/generalFormStore";
import { useGuestRegistrationStore } from "@/stores/guestRegistrationStore";
import {
  PREVIEW_FORM,
  PREVIEW_FORM_ID,
  PREVIEW_GUEST_ENTRIES,
} from "@/lib/previewFixtures";
import { convertApiFormDataToLocal } from "@/utils/transformApiData";
import { setPreviewMode } from "@/lib/previewMode";
import type { BookingDetails, FormMetadata } from "@/types/form";

interface PreviewDataContextValue {
  isPreviewData: boolean;
  kind: "listing" | "form";
}

const PreviewDataContext = createContext<PreviewDataContextValue>({
  isPreviewData: false,
  kind: "listing",
});

export const usePreviewData = () => useContext(PreviewDataContext);

function buildFormMetadata(): FormMetadata {
  return {
    formId: PREVIEW_FORM_ID,
    publicId: PREVIEW_FORM_ID,
    templateId: PREVIEW_FORM.templateId,
    listingTitle: PREVIEW_FORM.listingTitle,
    checkInDate: PREVIEW_FORM.checkInDate,
    checkOutDate: PREVIEW_FORM.checkOutDate,
    numberOfGuests: PREVIEW_FORM.numberOfGuests,
    maxVehicles: PREVIEW_FORM.maxVehicles,
    allowPets: PREVIEW_FORM.allowPets,
    listingAddress: PREVIEW_FORM.listingAddress,
    listingCity: PREVIEW_FORM.listingCity,
    listingCountry: PREVIEW_FORM.listingCountry,
    latitude: PREVIEW_FORM.latitude,
    longitude: PREVIEW_FORM.longitude,
    status: PREVIEW_FORM.status,
    version: PREVIEW_FORM.version,
    requiredGuestImages: PREVIEW_FORM.requiredGuestImages,
    requiredBookingContract: PREVIEW_FORM.requiredBookingContract,
    signedContract: PREVIEW_FORM.signedContract,
    minAgeToRequireImages: PREVIEW_FORM.minAgeToRequireImages,
    requireContactDataFromAllGuests: PREVIEW_FORM.requireContactDataFromAllGuests,
    overallProgress: PREVIEW_FORM.overallProgress,
    contractStatus: PREVIEW_FORM.contractStatus,
    rejectionReason: PREVIEW_FORM.rejectionReason,
    formMessages: PREVIEW_FORM.formMessages,
  };
}

function buildBookingDetails(): BookingDetails {
  return {
    listingTitle: PREVIEW_FORM.listingTitle,
    checkInDate: PREVIEW_FORM.checkInDate,
    checkOutDate: PREVIEW_FORM.checkOutDate,
    numberOfGuests: PREVIEW_FORM.numberOfGuests,
    maxVehicles: PREVIEW_FORM.maxVehicles,
    allowPets: PREVIEW_FORM.allowPets,
    listingAddress: PREVIEW_FORM.listingAddress,
    listingCity: PREVIEW_FORM.listingCity,
    listingCountry: PREVIEW_FORM.listingCountry,
    latitude: PREVIEW_FORM.latitude,
    longitude: PREVIEW_FORM.longitude,
  };
}

// WHY hydrate BEFORE children mount (lazy useState initializer): FormPage's first
// render reads formMetadata/formData/formLoaded. If we hydrated inside a useEffect,
// the first render would see the empty store, FormPage would call initializeForm
// (which now short-circuits in preview mode), and the user would see the loading
// skeleton flash before the second render picked up the hydrated data. Synchronous
// hydration on construction eliminates the flash.
function hydrateFormStore(): void {
  setPreviewMode(true);
  const metadata = buildFormMetadata();
  const bookingDetails = buildBookingDetails();
  const formData = convertApiFormDataToLocal(PREVIEW_FORM.data, PREVIEW_GUEST_ENTRIES);

  useGeneralFormStore.setState({
    formId: PREVIEW_FORM_ID,
    formData,
    formMetadata: metadata,
    bookingDetails,
    brandSlug: null,
    brandAutoRedirect: true,
    formLoaded: true,
    formState: {
      isLoading: false,
      isSaving: false,
      isSubmitting: false,
      hasUnsavedChanges: false,
      lastSavedVersion: PREVIEW_FORM.version,
      currentVersion: PREVIEW_FORM.version,
      currentSection: "",
      sections: {},
      versionMismatch: false,
    },
  });

  useGuestRegistrationStore.setState({
    guests: PREVIEW_GUEST_ENTRIES,
    guestCount: {
      current: PREVIEW_GUEST_ENTRIES.length,
      limit: PREVIEW_FORM.numberOfGuests,
      remaining: Math.max(PREVIEW_FORM.numberOfGuests - PREVIEW_GUEST_ENTRIES.length, 0),
    },
    isLoading: false,
    isCreating: false,
    isDeleting: false,
    isDesignating: false,
  });
}

function hydrateListingStore(): void {
  setPreviewMode(true);
}

function clearStores(): void {
  setPreviewMode(false);
  useGeneralFormStore.getState().reset();
  useGuestRegistrationStore.setState({
    guests: [],
    guestCount: { current: 0, limit: 0, remaining: 0 },
    isLoading: false,
    isCreating: false,
    isDeleting: false,
    isDesignating: false,
  });
}

interface PreviewDataProviderProps {
  kind: "listing" | "form";
  children: ReactNode;
}

const PreviewBanner: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div
      data-testid="preview-mode-banner"
      className="fixed top-0 inset-x-0 z-[100] bg-amber-500 text-amber-950 text-center text-xs font-semibold py-1 shadow-sm pointer-events-none"
    >
      {t("preview.banner", "Preview mode — submissions are disabled")}
    </div>
  );
};

export function PreviewDataProvider({ kind, children }: PreviewDataProviderProps) {
  // WHY lazy initializer: hydrate before children mount so the first render of
  // FormPage / GuestCheckinPage already sees populated store state — no skeleton flash.
  useState(() => {
    if (kind === "form") {
      hydrateFormStore();
    } else {
      hydrateListingStore();
    }
    return null;
  });

  useEffect(() => {
    return () => {
      clearStores();
    };
  }, []);

  const value = useMemo<PreviewDataContextValue>(
    () => ({ isPreviewData: true, kind }),
    [kind],
  );

  return (
    <PreviewDataContext.Provider value={value}>
      <PreviewBanner />
      {children}
    </PreviewDataContext.Provider>
  );
}
